import { useCallback, useEffect, useRef, useState } from "react";
import { loadMfe } from "@infrastructure/mfe/mfeLoader";
import { MFE_REGISTRY } from "@infrastructure/mfe/registry";
import { Spinner } from "@boundary/components/spinner";
import styles from "./photoEditorOverlay.module.scss";

interface PhotoEditorOverlayProps {
  open: boolean;
  imageUrl?: string;
  onClose: () => void;
}

export function PhotoEditorOverlay({ open, imageUrl, onClose }: PhotoEditorOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const unmountRef = useRef<(() => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const mountEditor = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;

    setLoading(true);
    setError(null);

    try {
      const { url, globalKey } = MFE_REGISTRY.photoEditor;
      const mod = await loadMfe(url, globalKey);
      if (!containerRef.current) return;

      unmountRef.current = mod.mount(el, {
        shell: {
          nav: { go: () => {} },
        },
        imageUrl,
        onComplete: () => {
          onClose();
        },
        onCancel: () => {
          onClose();
        },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось загрузить редактор");
    } finally {
      setLoading(false);
    }
  }, [onClose]);

  useEffect(() => {
    if (open) {
      mountEditor();
    }
    return () => {
      unmountRef.current?.();
      unmountRef.current = null;
    };
  }, [open, mountEditor]);

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        aria-label="Закрыть"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
      {loading && (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      )}
      {error && (
        <div className={styles.error}>
          <p>{error}</p>
          <button type="button" onClick={mountEditor} className={styles.retryBtn}>
            Повторить
          </button>
        </div>
      )}
      <div ref={containerRef} className={styles.container} />
    </div>
  );
}
